import { useMobileWidth } from '@utils/hooks';
import {
  RecommendedForYouCarouselRoot,
  CarouselWrapper,
  Title,
  Underline,
  UnderlineContainer,
  ItemWrapper,
  ItemTitle,
  ItemSubtitle,
  TextRow,
  Video,
  Image,
  MediaCard,
  CustomItemContainer,
  ProgressBarWrapper,
  ProgressBarContainer,
  ProgressFill,
} from './styles';

import { useState } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { useTheme } from 'styled-components';

export interface CarouselItemProps {
  id: string;
  description: string;
  eventName: string;
  link: string;
  themeColor: string;
  textLeft?: string;
  textRight?: string;
}

export interface RecommendedForYouCarouselProps {
  items?: CarouselItemProps[];
  title: string;
}

interface ProgressBarProps {
  current: number;
  total: number;
  color?: string;
}

export const ProgressBar = ({
  current,
  total,
  color = 'blue',
}: ProgressBarProps) => {
  const progress = total > 0 ? (current / total) * 100 : 0;

  return (
    <ProgressBarWrapper>
      <ProgressBarContainer>
        <ProgressFill width={progress} color={color} />
      </ProgressBarContainer>
    </ProgressBarWrapper>
  );
};

interface CustomItemProps {
  imageSrc?: string;
  videoSrc?: string;
  height?: string;
  width?: string;
  textLeft?: string;
  textRight?: string;
}

const CustomItem = ({
  imageSrc,
  videoSrc,
  height = '20rem',
  width = '100%',
  textLeft = 'WEBINAR  REPORT | Networking',
  textRight = '15:42',
}: CustomItemProps) => {
  return (
    <CustomItemContainer>
      <MediaCard height={height} width={width}>
        {videoSrc ? (
          <Video autoPlay muted loop playsInline>
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support HTML5 video.
          </Video>
        ) : (
          <Image src={imageSrc} alt={textLeft} />
        )}
      </MediaCard>

      <TextRow>
        <ItemTitle>{textLeft}</ItemTitle>
        <ItemSubtitle>{textRight}</ItemSubtitle>
      </TextRow>
    </CustomItemContainer>
  );
};

const RecommendedForYouCarousel = ({
  items = [],
  title,
}: RecommendedForYouCarouselProps) => {
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const [currentIndex, setCurrentIndex] = useState(1);

  const handleSlideChange = (nextSlide: number) => {
    setCurrentIndex(nextSlide + 1);
  };

  return (
    <RecommendedForYouCarouselRoot>
      <Title color={theme.color.mobilityGrey}>{title}</Title>

      {!isMobile && (
        <UnderlineContainer>
          <Underline color={theme.color.mobilityBlue} />
        </UnderlineContainer>
      )}

      <CarouselWrapper>
        <Carousel
          responsive={{
            desktop: {
              breakpoint: { max: 3000, min: 1024 },
              items: 4,
              slidesToSlide: 1,
            },
            tablet: {
              breakpoint: { max: 1024, min: 768 },
              items: 2,
              slidesToSlide: 1,
            },
            mobile: {
              breakpoint: { max: 768, min: 0 },
              items: 1,
              slidesToSlide: 1,
            },
          }}
          infinite={false}
          centerMode={false}
          beforeChange={handleSlideChange}
          arrows
        >
          {items?.map(item => (
            <ItemWrapper key={item.id}>
              <CustomItem
                height={'20rem'}
                width={'100%'}
                imageSrc={item.link}
                videoSrc=""
                textLeft={item.textLeft}
                textRight={item.textRight}
              />
            </ItemWrapper>
          ))}
        </Carousel>

        {isMobile && (
          <ProgressBar
            current={currentIndex}
            total={items.length}
            color={theme.color.mobilityBlue}
          />
        )}
      </CarouselWrapper>
    </RecommendedForYouCarouselRoot>
  );
};

export default RecommendedForYouCarousel;
